export interface BoxShadow {
  inset: boolean;
  offsetX: string;
  offsetY: string;
  blurRadius: string;
  spreadRadius: string;
  color: string | null;
}

export function parseCssColorToRgba(
  color: string,
): { r: number; g: number; b: number; a: number } | null {
  const ctx = document.createElement(`canvas`).getContext(`2d`);
  if (!ctx) return null;

  ctx.fillStyle = `#000`;
  ctx.fillStyle = color;
  const computed = ctx.fillStyle.trim();

  // Handle rgb/rgba output
  const rgbMatch = computed.match(
    /^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/,
  );
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10),
      g: parseInt(rgbMatch[2], 10),
      b: parseInt(rgbMatch[3], 10),
      a: rgbMatch[4] !== undefined ? parseFloat(rgbMatch[4]) : 1,
    };
  }

  // Handle hex output (#rgb, #rrggbb, #rrggbbaa)
  const hexMatch = computed.match(/^#([0-9a-f]{3,8})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];

    // Expand shorthand #rgb or #rgba → #rrggbb or #rrggbbaa
    if (hex.length === 3 || hex.length === 4) {
      hex = hex
        .split(``)
        .map((c) => c + c)
        .join(``);
    }

    // Convert to RGBA
    const intVal = parseInt(hex, 16);
    // eslint-disable-next-line no-bitwise
    const r = (intVal >> (hex.length === 8 ? 24 : 16)) & 255;
    // eslint-disable-next-line no-bitwise
    const g = (intVal >> (hex.length === 8 ? 16 : 8)) & 255;
    // eslint-disable-next-line no-bitwise
    const b = (intVal >> (hex.length === 8 ? 8 : 0)) & 255;
    const a =
      // eslint-disable-next-line no-bitwise
      hex.length === 8 ? Math.round(((intVal & 255) / 255) * 100) / 100 : 1;

    return { r, g, b, a };
  }

  return null;
}

export function parseSpacing(value: string): {
  top: string;
  right: string;
  bottom: string;
  left: string;
} {
  const parts = value.trim().split(/\s+/);
  let [top, right, bottom, left] = [``, ``, ``, ``];

  if (parts.length === 1) {
    // eslint-disable-next-line no-multi-assign,prefer-destructuring
    top = right = bottom = left = parts[0];
  } else if (parts.length === 2) {
    // eslint-disable-next-line no-multi-assign,prefer-destructuring
    top = bottom = parts[0];
    // eslint-disable-next-line no-multi-assign,prefer-destructuring
    right = left = parts[1];
  } else if (parts.length === 3) {
    // eslint-disable-next-line prefer-destructuring
    top = parts[0];
    // eslint-disable-next-line no-multi-assign,prefer-destructuring
    right = left = parts[1];
    // eslint-disable-next-line prefer-destructuring
    bottom = parts[2];
  } else if (parts.length === 4) {
    [top, right, bottom, left] = parts;
  }

  return { top, right, bottom, left };
}

export function parseBorderValue(borderValue: string) {
  const borderRegex = /^([\d.]+[a-z%]*)\s+([a-z]+)\s+(.+)$/i;
  const match = borderValue.match(borderRegex);

  if (!match) {
    return { width: ``, style: ``, color: `` };
  }

  const [, width, style, color] = match;

  return {
    width,
    style,
    color, // Replace with parsedColor if you want RGBA object
  };
}

function splitCommaSafe(str) {
  const out = [];
  let cur = ``;
  let depth = 0;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    // eslint-disable-next-line no-plusplus
    if (ch === `(`) depth++;
    if (ch === `)`) depth = Math.max(0, depth - 1);
    if (ch === `,` && depth === 0) {
      out.push(cur.trim());
      cur = ``;
    } else {
      cur += ch;
    }
  }
  if (cur.trim()) out.push(cur.trim());
  return out;
}

// Split by whitespace OUTSIDE of parentheses
function splitSpaceSafe(str) {
  const out = [];
  let cur = ``;
  let depth = 0;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    // eslint-disable-next-line no-plusplus
    if (ch === `(`) depth++;
    if (ch === `)`) depth = Math.max(0, depth - 1);
    if (/\s/.test(ch) && depth === 0) {
      if (cur) {
        out.push(cur);
        cur = ``;
      }
    } else {
      cur += ch;
    }
  }
  if (cur) out.push(cur);
  return out;
}

const COLOR_FN = /^(rgb|rgba|hsl|hsla|hwb|lab|lch|oklab|oklch|color)\(/i;
const HEX_COLOR = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const LENGTH_RE = /^-?(?:\d+(?:\.\d+)?|\.\d+)(?:[a-z]+|%)?$/i;

function isLengthToken(t) {
  if (LENGTH_RE.test(t)) return true;
  // treat calc()/min()/max()/clamp()/var() as length-like (heuristic)
  return /^(calc|min|max|clamp|var)\(/i.test(t);
}
function isExplicitColorToken(t) {
  if (COLOR_FN.test(t)) return true; // rgb(...), hsl(...), color(...)
  if (HEX_COLOR.test(t)) return true; // #fff, #ffffff, #ffffffff
  if (/^(transparent|currentColor)$/i.test(t)) return true;
  if (/^var\(/i.test(t)) return true; // allow CSS vars as color
  return false;
}

function parseOneShadow(shadow): BoxShadow {
  const tokens = splitSpaceSafe(shadow.trim());
  // handle 'inset' anywhere
  let inset = false;
  // eslint-disable-next-line no-plusplus
  for (let i = tokens.length - 1; i >= 0; i--) {
    if (/^inset$/i.test(tokens[i])) {
      inset = true;
      tokens.splice(i, 1);
    }
  }

  // Prefer explicit color tokens first
  let color = null;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < tokens.length; i++) {
    if (isExplicitColorToken(tokens[i])) {
      color = tokens[i];
      tokens.splice(i, 1);
      break;
    }
  }
  // If no explicit color, treat the last non-length token (if any) as color (e.g., 'red')
  if (color == null) {
    // eslint-disable-next-line no-plusplus
    for (let i = tokens.length - 1; i >= 0; i--) {
      if (!isLengthToken(tokens[i])) {
        color = tokens[i];
        tokens.splice(i, 1);
        break;
      }
    }
  }

  // The remaining tokens should be lengths: offset-x, offset-y, [blur], [spread]
  const lengths = tokens.filter(isLengthToken);
  const [offsetX, offsetY, blurRadius, spreadRadius] = lengths;

  if (offsetX == null || offsetY == null) {
    throw new Error(
      `Invalid box-shadow: "${shadow}" (missing required offsets)`,
    );
  }

  return {
    inset,
    offsetX,
    offsetY,
    blurRadius: blurRadius ?? `0`,
    spreadRadius: spreadRadius ?? `0`,
    color,
  };
}

export function parseBoxShadow(shadowStr: string): BoxShadow[] {
  if (!shadowStr || typeof shadowStr !== `string`) {
    throw new Error(`Invalid box-shadow string`);
  }
  const s = shadowStr.trim();
  if (s.toLowerCase() === `none`) return [];
  return splitCommaSafe(s).map(parseOneShadow);
}

export interface TextShadow {
  offsetX: string;
  offsetY: string;
  blurRadius: string;
  color: string | null;
}

function parseOneTextShadow(shadow): TextShadow {
  const tokens = splitSpaceSafe(shadow.trim());

  // Prefer explicit color tokens first
  let color: string | null = null;
  for (let i = 0; i < tokens.length; i += 1) {
    if (isExplicitColorToken(tokens[i])) {
      color = tokens[i];
      tokens.splice(i, 1);
      break;
    }
  }

  // If no explicit color, treat the last non-length token (if any) as color (e.g., 'red')
  if (color == null) {
    for (let i = tokens.length - 1; i >= 0; i -= 1) {
      if (!isLengthToken(tokens[i])) {
        color = tokens[i];
        tokens.splice(i, 1);
        break;
      }
    }
  }

  // Remaining tokens should be lengths: offset-x, offset-y, [blur]
  const lengths = tokens.filter(isLengthToken);
  const [offsetX, offsetY, blurRadius] = lengths;

  if (offsetX == null || offsetY == null) {
    throw new Error(
      `Invalid text-shadow: "${shadow}" (missing required offsets)`,
    );
  }

  return {
    offsetX,
    offsetY,
    blurRadius: blurRadius ?? `0`,
    color,
  };
}

export function parseTextShadow(shadowStr: string): TextShadow[] {
  if (!shadowStr || typeof shadowStr !== `string`) {
    throw new Error(`Invalid text-shadow string`);
  }
  const s = shadowStr.trim();
  if (s.toLowerCase() === `none`) return [];
  return splitCommaSafe(s).map(parseOneTextShadow);
}
